<template>
  <v-dialog v-if="dialog" v-model="dialog" persistent fullscreen scrollable transition="dialog-bottom-transition">
    <!-- Delete-from-matches confirmation dialog -->
    <v-dialog v-model="deleteFromSemanticConfirmDialog" max-width="420" persistent>
      <v-card>
        <v-card-title class="py-3">
          <v-icon left color="error">mdi-link-off</v-icon>
          Remove from matches?
        </v-card-title>
        <v-card-text class="pb-2">
          <p class="mb-3">
            This will unmatch
            <strong>{{ deleteFromSemanticResult && deleteFromSemanticResult.source.name }}</strong>
            from
            <strong>{{ deleteFromSemanticResult && (deleteFromSemanticResult.source.websiteName || deleteFromSemanticResult.source.websiteAlias) }}</strong>.
          </p>
          <p class="mb-0 caption grey--text">The product will be marked for deletion when you save.</p>
        </v-card-text>
        <v-card-actions class="pt-0 px-4 pb-3">
          <v-spacer />
          <v-btn text small class="text-none" @click="deleteFromSemanticConfirmDialog = false">Cancel</v-btn>
          <v-btn
            depressed
            small
            color="error"
            class="text-none white--text"
            @click="confirmDeleteFromSemantic"
          >
            <v-icon left small>mdi-link-off</v-icon>
            Yes, remove
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="swapConfirmDialog" max-width="620" persistent>
      <v-card>
        <v-card-title class="py-3">
          <v-icon left color="warning">mdi-swap-horizontal-circle</v-icon>
          Replace existing match?
        </v-card-title>
        <v-card-text class="pb-2">
          <div class="d-flex align-stretch" style="gap: 12px">
            <!-- Current/old -->
            <div class="swap-product-card swap-product-card--old flex-1">
              <div class="swap-product-card__label mb-2">
                <v-icon small color="error" class="mr-1">mdi-link-off</v-icon>
                <span class="body-2 font-weight-bold error--text">Replacing</span>
              </div>
              <div v-if="swapExistingProduct">
                <div class="swap-product-card__img-wrap mb-2">
                  <img
                    v-if="swapExistingProduct.product && swapExistingProduct.product.image"
                    :src="swapExistingProduct.product.image"
                    class="swap-product-card__img"
                  />
                  <v-icon v-else size="40" color="grey lighten-2">mdi-image-off</v-icon>
                </div>
                <a
                  v-if="swapExistingProduct.product && swapExistingProduct.product.url"
                  :href="swapExistingProduct.product.url"
                  target="_blank"
                  class="swap-product-card__name body-2 font-weight-medium d-block mb-1"
                  @click.stop
                >{{ swapExistingProduct.product.name }}</a>
                <div v-else class="body-2 font-weight-medium mb-1">{{ swapExistingProduct.product && swapExistingProduct.product.name }}</div>
                <div class="caption grey--text mb-1">{{ swapExistingProduct.website && swapExistingProduct.website.alias }}</div>
                <div
                  v-if="swapExistingProduct.product && swapExistingProduct.product.offers && swapExistingProduct.product.offers.length"
                  class="body-1 font-weight-bold"
                >
                  {{ swapExistingProduct.product.offers[0].price }}
                  <span class="caption grey--text font-weight-regular">{{ swapExistingProduct.product.offers[0].currency }}</span>
                </div>
              </div>
              <div v-else class="body-2 grey--text font-italic">Unknown existing match</div>
            </div>

            <div class="d-flex align-center justify-center" style="flex: 0 0 auto">
              <v-icon color="warning" size="28">mdi-arrow-right-bold</v-icon>
            </div>

            <!-- New -->
            <div class="swap-product-card swap-product-card--new flex-1">
              <div class="swap-product-card__label mb-2">
                <v-icon small color="success" class="mr-1">mdi-link-plus</v-icon>
                <span class="body-2 font-weight-bold success--text">New match</span>
              </div>
              <div v-if="swapConfirmResult">
                <div class="swap-product-card__img-wrap mb-2">
                  <img
                    v-if="swapConfirmResult.source.image"
                    :src="swapConfirmResult.source.image"
                    class="swap-product-card__img"
                  />
                  <v-icon v-else size="40" color="grey lighten-2">mdi-image-off</v-icon>
                </div>
                <a
                  :href="'https://' + swapConfirmResult.source.websiteAlias + swapConfirmResult.source.url"
                  target="_blank"
                  class="swap-product-card__name body-2 font-weight-medium d-block mb-1"
                  @click.stop
                >{{ swapConfirmResult.source.name }}</a>
                <div class="caption grey--text mb-1">{{ swapConfirmResult.source.websiteName || swapConfirmResult.source.websiteAlias }}</div>
                <div
                  v-if="swapConfirmResult.source.offers && swapConfirmResult.source.offers.length"
                  class="body-1 font-weight-bold"
                >
                  {{ swapConfirmResult.source.offers[0].price }}
                  <span class="caption grey--text font-weight-regular">{{ swapConfirmResult.source.offers[0].currency }}</span>
                </div>
              </div>
            </div>
          </div>
        </v-card-text>
        <v-card-actions class="pt-2 px-4 pb-3">
          <v-spacer />
          <v-btn text small class="text-none" @click="swapConfirmDialog = false">Cancel</v-btn>
          <v-btn
            depressed
            small
            color="warning"
            class="text-none white--text"
            @click="confirmSwap"
          >
            <v-icon left small>mdi-swap-horizontal</v-icon>
            Yes, replace
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="addConfirmDialog" max-width="420" persistent>
      <v-card>
        <v-card-title class="py-3">
          <v-icon left color="primary">mdi-plus-circle-outline</v-icon>
          Add as new match?
        </v-card-title>
        <v-card-text class="pb-2">
          <div class="swap-product-card swap-product-card--new" v-if="addConfirmResult">
            <div class="swap-product-card__label mb-2">
              <v-icon small color="success" class="mr-1">mdi-link-plus</v-icon>
              <span class="body-2 font-weight-bold success--text">New match</span>
            </div>
            <div class="swap-product-card__img-wrap mb-2">
              <img
                v-if="addConfirmResult.source.image"
                :src="addConfirmResult.source.image"
                class="swap-product-card__img"
              />
              <v-icon v-else size="40" color="grey lighten-2">mdi-image-off</v-icon>
            </div>
            <a
              :href="'https://' + addConfirmResult.source.websiteAlias + addConfirmResult.source.url"
              target="_blank"
              class="swap-product-card__name body-2 font-weight-medium d-block mb-1"
              @click.stop
            >{{ addConfirmResult.source.name }}</a>
            <div class="caption grey--text mb-1">{{ addConfirmResult.source.websiteName || addConfirmResult.source.websiteAlias }}</div>
            <div
              v-if="addConfirmResult.source.offers && addConfirmResult.source.offers.length"
              class="body-1 font-weight-bold"
            >
              {{ addConfirmResult.source.offers[0].price }}
              <span class="caption grey--text font-weight-regular">{{ addConfirmResult.source.offers[0].currency }}</span>
            </div>
          </div>
        </v-card-text>
        <v-card-actions class="pt-2 px-4 pb-3">
          <v-spacer />
          <v-btn text small class="text-none" @click="addConfirmDialog = false; addConfirmResult = null">Cancel</v-btn>
          <v-btn
            depressed
            small
            color="primary"
            class="text-none"
            @click="confirmAdd"
          >
            <v-icon left small>mdi-plus-circle-outline</v-icon>
            Yes, add
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="deleteCurrentConfirmDialog" max-width="420" persistent>
      <v-card>
        <v-card-title class="py-3">
          <v-icon left color="error">mdi-link-off</v-icon>
          Remove match?
        </v-card-title>
        <v-card-text class="pb-2" v-if="deleteCurrentResult">
          <div class="swap-product-card swap-product-card--old">
            <div class="swap-product-card__img-wrap mb-2">
              <img
                v-if="deleteCurrentResult.product && deleteCurrentResult.product.image"
                :src="deleteCurrentResult.product.image"
                class="swap-product-card__img"
              />
              <v-icon v-else size="40" color="grey lighten-2">mdi-image-off</v-icon>
            </div>
            <a
              v-if="deleteCurrentResult.product && deleteCurrentResult.product.url"
              :href="deleteCurrentResult.product.url"
              target="_blank"
              class="swap-product-card__name body-2 font-weight-medium d-block mb-1"
              @click.stop
            >{{ deleteCurrentResult.product.name }}</a>
            <div class="caption grey--text mb-1">{{ deleteCurrentResult.website && deleteCurrentResult.website.alias }}</div>
            <div
              v-if="deleteCurrentResult.product && deleteCurrentResult.product.offers && deleteCurrentResult.product.offers.length"
              class="body-1 font-weight-bold"
            >
              {{ deleteCurrentResult.product.offers[0].price }}
              <span class="caption grey--text font-weight-regular">{{ deleteCurrentResult.product.offers[0].currency }}</span>
            </div>
          </div>
        </v-card-text>
        <v-card-actions class="pt-2 px-4 pb-3">
          <v-spacer />
          <v-btn text small class="text-none" @click="deleteCurrentConfirmDialog = false; deleteCurrentResult = null">Cancel</v-btn>
          <v-btn depressed small color="error" class="text-none" @click="confirmDeleteCurrent">
            <v-icon left small>mdi-link-off</v-icon>
            Yes, remove
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-card class="validate-modal-card">
      <v-card-text class="pa-4">        <v-container fluid class="pa-0">
          <!-- Loading / Submitting states -->
          <div v-if="submit_loading" class="mb-4">
            <v-progress-linear
              indeterminate
              color="primary"
              style="pointer-events: none"
              rounded
            ></v-progress-linear>
            <div class="text-center caption grey--text mt-2">Updating...</div>
          </div>

          <div v-if="!submit_loading">
            <loadingProgress v-if="loading" :loading="loading" text="Loading..." />

            <template v-else>
              <!-- Reference Product Section -->
              <v-card flat outlined class="reference-section mb-4">
                <v-card-text class="pa-3">
                  <div class="d-flex align-center">
                    <!-- Image with stock dot -->
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-badge
                          v-bind="attrs"
                          v-on="on"
                          dot
                          :color="
                            defaultProduct.product.offers[0].availability == 'InStock'
                              ? 'success'
                              : 'error'
                          "
                          overlap
                          bordered
                          offset-x="10"
                          offset-y="10"
                        >
                          <div class="reference-image-wrapper">
                            <img
                              class="reference-product-image"
                              :src="defaultProduct.product.image"
                            />
                          </div>
                        </v-badge>
                      </template>
                      <span>{{ defaultProduct.product.offers[0].availability }}</span>
                    </v-tooltip>

                    <!-- Info -->
                    <div class="ml-4 flex-grow-1" style="min-width: 0">
                      <div class="d-flex align-center mb-1">
                        <img
                          :src="getWebsiteFavicon(defaultProduct.website.url)"
                          :alt="defaultProduct.website.alias"
                          height="20"
                          class="mr-2 favicon-img"
                        />
                        <a
                          :href="defaultProduct.product.url"
                          target="_blank"
                          class="text-decoration-none caption"
                        >
                          {{ defaultProduct.website.alias }}
                        </a>
                      </div>
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <span
                            v-bind="attrs"
                            v-on="on"
                            class="text-subtitle-2 font-weight-medium d-block text-truncate"
                          >
                            {{ defaultProduct.product.name }}
                          </span>
                        </template>
                        <span>{{ defaultProduct.product.name }}</span>
                      </v-tooltip>
                      <div class="mt-1 d-flex flex-wrap">
                        <v-chip
                          v-if="defaultProduct.product.brand"
                          label
                          x-small
                          outlined
                          class="mr-1 mb-1"
                          >{{ defaultProduct.product.brand }}</v-chip
                        >
                        <v-chip
                          v-if="defaultProduct.product.user_ref"
                          label
                          x-small
                          outlined
                          class="mr-1 mb-1"
                          >SKU: {{ defaultProduct.product.user_ref }}</v-chip
                        >
                        <v-chip
                          v-if="defaultProduct.product.model"
                          label
                          x-small
                          outlined
                          class="mr-1 mb-1"
                          >MPN: {{ defaultProduct.product.model }}</v-chip
                        >
                        <v-chip
                          v-if="defaultProduct.product.ean"
                          label
                          x-small
                          outlined
                          class="mr-1 mb-1"
                          >EAN: {{ defaultProduct.product.ean }}</v-chip
                        >
                      </div>
                    </div>

                    <!-- Price + actions -->
                    <div class="d-flex align-center ml-4" style="flex-shrink: 0; gap: 8px">
                      <div class="text-right">
                        <span
                          v-html="
                            priceFormatter(
                              defaultProduct,
                              ProductToEdit?.hight_price,
                              ProductToEdit?.low_price,
                              true,
                            )
                          "
                        ></span>
                        <div
                          class="caption text--secondary"
                          v-if="defaultProduct.product.offers && defaultProduct.product.offers.length"
                        >
                          {{ defaultProduct.product.offers[0].changeDate }}
                        </div>
                      </div>
                      <v-divider vertical class="mx-1" style="height: 32px; align-self: center" />
                      <v-btn icon small @click="showEditModal(ProductToEdit)">
                        <v-icon small color="primary">mdi-pencil</v-icon>
                      </v-btn>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
              <!-- Competitors Table -->
              <v-card flat outlined class="competitors-section">
                <v-card-title class="grey lighten-4 py-2 flex-wrap" style="gap: 8px">
                  <v-btn-toggle v-model="activeView" mandatory dense class="elevation-0">
                    <v-btn value="current" small class="text-none px-3">
                      <v-icon left x-small>mdi-account-group-outline</v-icon>
                      Current Matches
                    </v-btn>
                    <v-btn value="semantic" small class="text-none px-3">
                      <v-icon left x-small>mdi-brain</v-icon>
                      Semantic Search
                      <span class="beta-badge ml-1">BETA</span>
                      <v-chip
                        v-if="semanticAdded.length"
                        x-small
                        color="primary"
                        class="ml-1 white--text"
                      >+{{ semanticAdded.length }}</v-chip>
                    </v-btn>
                  </v-btn-toggle>
                  <v-spacer />
                  <template v-if="activeView === 'semantic'">
                    <v-text-field
                      v-model="semanticQuery"
                      dense
                      outlined
                      hide-details
                      clearable
                      placeholder="Search query…"
                      prepend-inner-icon="mdi-magnify"
                      class="mr-2"
                      style="max-width: 260px; font-size: 12px"
                      @keydown.enter="fetchSemanticSimilarities"
                    />
                    <v-select
                      v-model="semanticMinScore"
                      :items="[
                        { text: 'All scores', value: null },
                        { text: 'High (≥80%)', value: 0.8 },
                        { text: 'Medium (≥60%)', value: 0.6 },
                        { text: 'Low (≥40%)', value: 0.4 },
                      ]"
                      clearable
                      dense
                      outlined
                      hide-details
                      class="mr-2"
                      style="max-width: 160px; font-size: 12px"
                    />
                    <v-select
                      v-model="semanticSelectedWebsites"
                      :items="semanticWebsites"
                      :loading="semanticWebsitesLoading"
                      dense
                      outlined
                      hide-details
                      multiple
                      clearable
                      placeholder="All websites"
                      prepend-inner-icon="mdi-web"
                      class="mr-2"
                      style="max-width: 220px; font-size: 12px"
                      @change="fetchSemanticSimilarities"
                    >
                      <template v-slot:selection="{ item, index }">
                        <span v-if="index === 0" class="caption text-truncate" style="max-width: 110px">{{ item }}</span>
                        <span v-if="index === 1" class="caption grey--text ml-1">(+{{ semanticSelectedWebsites.length - 1 }})</span>
                      </template>
                    </v-select>
                    <!-- Price range filter -->
                    <v-menu
                      v-model="semanticPriceMenuOpen"
                      :close-on-content-click="false"
                      offset-y
                      left
                      min-width="280"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          v-bind="attrs"
                          v-on="on"
                          small
                          outlined
                          :color="semanticPriceFilterActive ? 'primary' : 'grey'"
                          class="mr-2 text-none"
                          style="font-size: 11px; height: 32px"
                        >
                          <v-icon x-small left>mdi-cash-multiple</v-icon>
                          <template v-if="semanticPriceFilterActive">
                            {{ semanticPriceRange[0] > 0 ? '+' : '' }}{{ semanticPriceRange[0] }}% /
                            {{ semanticPriceRange[1] > 0 ? '+' : '' }}{{ semanticPriceRange[1] }}%
                          </template>
                          <template v-else>Price range</template>
                        </v-btn>
                      </template>
                      <v-card class="pa-4" style="min-width: 280px">
                        <div class="caption font-weight-bold mb-1">Price difference vs. reference</div>
                        <div class="caption grey--text mb-3">
                          Show results where competitor price differs from your price by:
                        </div>
                        <v-range-slider
                          v-model="semanticPriceRange"
                          :min="-100"
                          :max="100"
                          :step="5"
                          thumb-label="always"
                          thumb-size="32"
                          color="primary"
                          track-color="grey lighten-2"
                          class="mt-6"
                        >
                          <template v-slot:thumb-label="{ value }">
                            <span style="font-size: 9px; white-space: nowrap">
                              {{ value > 0 ? '+' : '' }}{{ value }}%
                            </span>
                          </template>
                        </v-range-slider>
                        <div class="d-flex justify-space-between caption grey--text mt-1">
                          <span>-100%</span>
                          <span>0%</span>
                          <span>+100%</span>
                        </div>
                        <div class="d-flex justify-end mt-3">
                          <v-btn
                            x-small text color="grey"
                            class="text-none mr-2"
                            @click="semanticPriceRange = [-100, 100]"
                          >Clear</v-btn>
                          <v-btn
                            x-small depressed color="primary"
                            class="text-none"
                            @click="semanticPriceMenuOpen = false; fetchSemanticSimilarities()"
                          >Apply</v-btn>
                        </div>
                      </v-card>
                    </v-menu>
                    <v-btn
                      small
                      outlined
                      color="grey darken-1"
                      class="mr-2 text-none"
                      style="font-size: 11px; height: 32px"
                      :disabled="semanticLoading"
                      @click="resetSemanticSearch"
                    >
                      <v-icon x-small left>mdi-restore</v-icon>
                      Reset filters
                    </v-btn>
                    <v-btn-toggle
                      v-model="semanticViewMode"
                      mandatory dense
                      class="mr-2 elevation-0"
                      @change="v => localStorage.setItem('semanticViewMode', v)"
                    >
                      <v-btn value="table" small height="32" class="px-2" title="Table view">
                        <v-icon x-small>mdi-table-row</v-icon>
                      </v-btn>
                      <v-btn value="columns" small height="32" class="px-2" title="Column view">
                        <v-icon x-small>mdi-view-column-outline</v-icon>
                      </v-btn>
                    </v-btn-toggle>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          v-bind="attrs"
                          v-on="on"
                          icon
                          small
                          :loading="semanticLoading"
                          @click="fetchSemanticSimilarities"
                        >
                          <v-icon small>mdi-refresh</v-icon>
                        </v-btn>
                      </template>
                      <span>Re-run semantic search</span>
                    </v-tooltip>
                  </template>
                </v-card-title>
                <v-card-text class="pa-0">
                  <!-- ── Current Matches view ── -->
                  <v-simple-table v-if="activeView === 'current'" fixed-header height="65vh">
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th
                            class="text-center grey lighten-4 font-weight-medium text-caption"
                            style="width: 60px"
                          >
                            Match
                          </th>
                          <th
                            class="grey lighten-4 font-weight-medium text-caption"
                            style="width: 100px"
                          >
                            Image
                          </th>
                          <th class="grey lighten-4 font-weight-medium text-caption">Competitor</th>
                          <th class="grey lighten-4 font-weight-medium text-caption">
                            Product Details
                          </th>
                          <th class="grey lighten-4 font-weight-medium text-caption">Price</th>
                          <th class="text-center grey lighten-4 font-weight-medium text-caption">
                            Stock
                          </th>
                          <th class="text-center grey lighten-4 font-weight-medium text-caption">
                            Source
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="(competitor, i) in filteredCompetitors"
                          :key="competitor.website.id"
                          :ref="i === 0 ? 'firstProduct' : ''"
                          class="tabel-row position-relative"
                          :class="[competitor.product.not_found ? 'not_found_product' : '']"
                          @click="askDeleteProduct(competitor)"
                        >
                          <td>
                            <div class="row-overlay" v-if="competitor.product.delete">
                              <v-btn
                                color="success"
                                small
                                outlined
                                class="font-weight-bold bg-white text-none"
                              >
                                <v-icon left small>mdi-check</v-icon>
                                Confirm Match
                              </v-btn>
                              <v-btn
                                color="primary"
                                small
                                outlined
                                class="font-weight-bold bg-white text-none ml-2"
                                :href="competitor.product.url"
                                target="_blank"
                                @click.stop
                              >
                                <v-icon left small>mdi-open-in-new</v-icon>
                                Inspect
                              </v-btn>
                            </div>
                            <div v-bind:key="incrSelectionAction" class="d-flex justify-center">
                              <v-tooltip right>
                                <template v-slot:activator="{ on, attrs }">
                                  <v-btn
                                    v-bind="attrs"
                                    v-on="on"
                                    icon
                                    large
                                    @click.stop="askDeleteProduct(competitor)"
                                  >
                                    <v-icon
                                      :color="
                                        !competitor.product.delete ? 'success' : 'grey lighten-1'
                                      "
                                      size="28"
                                    >
                                      {{
                                        !competitor.product.delete
                                          ? 'mdi-check-circle'
                                          : 'mdi-checkbox-blank-circle-outline'
                                      }}
                                    </v-icon>
                                  </v-btn>
                                </template>
                                <span>{{
                                  competitor.product.delete ? 'Mark as Match' : 'Unmatch'
                                }}</span>
                              </v-tooltip>
                            </div>
                          </td>
                          <td class="pa-1 w-10">
                            <div
                              class="w-100 d-flex justify-center"
                              v-if="competitor.product.image"
                            >
                              <img class="product-image" :src="competitor.product.image" />
                            </div>
                          </td>
                          <td class="pa-1 w-15">
                            <div class="d-flex align-center">
                              <img
                                :src="getWebsiteFavicon(competitor.website.url)"
                                :alt="competitor.website.alias"
                                height="16"
                                class="mr-2 favicon-img"
                              />
                              <v-icon
                                class="mr-2"
                                size="medium"
                                :color="competitor.website.config.color"
                                >$circle</v-icon
                              >
                              <span>
                                <a :href="competitor.product.url" target="_balank">
                                  {{ competitor.website.alias }}
                                </a>
                              </span>
                            </div>
                          </td>
                          <td class="pa-1 more-info w-40">
                            <div>
                              <v-tooltip bottom>
                                <span>{{ competitor.product.name }}</span>
                                <template v-slot:activator="{ on, attrs }">
                                  <span
                                    v-bind="attrs"
                                    v-on="on"
                                    class="d-inline-block"
                                    style="max-width: 100%"
                                  >
                                    {{ competitor.product.name }}
                                  </span>
                                  <div>
                                    <v-chip
                                      class="mr-1"
                                      label
                                      x-small
                                      outlined
                                      v-for="(
                                        identifier, i
                                      ) in competitor.product.meta.identifiers.slice(0, 5)"
                                      v-bind:key="i"
                                      color="grey"
                                      >{{ identifier }}
                                    </v-chip>
                                  </div>
                                  <div
                                    class="mt-2 d-flex align-center flex-wrap"
                                    v-if="
                                      competitor.product.meta.matching_confidence ||
                                      (competitor.product.delete &&
                                        competitor.product.name_similarity_score != null)
                                    "
                                  >
                                    <template v-if="competitor.product.meta.matching_confidence">
                                      <span class="caption grey--text mr-2">Confidence:</span>
                                      <v-progress-linear
                                        :value="competitor.product.meta.matching_confidence * 100"
                                        :color="
                                          competitor.product.meta.matching_confidence > 0.8
                                            ? 'success'
                                            : 'warning'
                                        "
                                        height="12"
                                        style="width: 90px; border-radius: 4px"
                                        rounded
                                      >
                                        <template v-slot:default="{ value }">
                                          <strong class="white--text" style="font-size: 10px"
                                            >{{ Math.ceil(value) }}%</strong
                                          >
                                        </template>
                                      </v-progress-linear>
                                    </template>

                                    <template
                                      v-if="
                                        competitor.product.delete &&
                                        competitor.product.name_similarity_score != null
                                      "
                                    >
                                      <span
                                        class="caption grey--text mr-2"
                                        :class="{
                                          'ml-4': competitor.product.meta.matching_confidence,
                                        }"
                                      >
                                        <v-icon x-small color="grey" class="mr-1"
                                          >mdi-text-search</v-icon
                                        >
                                        Similarity score:
                                      </span>
                                      <v-chip
                                        x-small
                                        label
                                        :color="
                                          competitor.product.name_similarity_score > 0.8
                                            ? 'success'
                                            : competitor.product.name_similarity_score > 0.5
                                            ? 'warning'
                                            : 'error'
                                        "
                                        :text-color="
                                          competitor.product.name_similarity_score > 0.5
                                            ? 'white'
                                            : 'white'
                                        "
                                      >
                                        {{
                                          (competitor.product.name_similarity_score * 100).toFixed(
                                            0,
                                          )
                                        }}%
                                      </v-chip>
                                    </template>
                                  </div>
                                </template>
                              </v-tooltip>
                            </div>
                          </td>
                          <td class="pa-2">
                            <span
                              v-html="
                                priceFormatter(
                                  competitor,
                                  ProductToEdit?.hight_price,
                                  ProductToEdit?.low_price,
                                  true,
                                )
                              "
                            ></span>
                            <div
                              class="sm-text tex--secondary"
                              v-if="competitor.product.offers && competitor.product.offers.length"
                            >
                              {{ competitor.product.offers[0].changeDate }}
                            </div>
                          </td>
                          <td class="text-center pa-0">
                            <badge
                              class="sm-text"
                              small="true"
                              :text="competitor.product.offers[0].availability"
                              :color="
                                competitor.product.offers[0].availability == 'InStock'
                                  ? 'success'
                                  : 'error'
                              "
                            />
                          </td>
                          <td class="text-center">
                            <v-tooltip
                              v-if="competitor.product.meta.matched_by == 'auto-matching-openai'"
                              top
                            >
                              <template v-slot:activator="{ on, attrs }">
                                <v-icon v-bind="attrs" v-on="on" size="medium" color="primary"
                                  >mdi-robot-outline</v-icon
                                >
                              </template>
                              <span>Confirmed by AI</span>
                            </v-tooltip>
                            <v-tooltip
                              v-if="
                                competitor.product.meta.matched_by == 'auto-matching-score' ||
                                competitor.product.meta.matched_by == ''
                              "
                              top
                            >
                              <template v-slot:activator="{ on, attrs }">
                                <v-icon v-bind="attrs" v-on="on" size="medium" color="primary"
                                  >mdi-arrow-decision-auto-outline</v-icon
                                >
                              </template>
                              <span>Auto confirmation</span>
                            </v-tooltip>
                          </td>
                        </tr>
                        <tr
                          v-if="competitors.length > filteredCompetitors.length || !showOnlyPending"
                        >
                          <td colspan="7" class="text-center pa-2">
                            <v-btn
                              outlined
                              small
                              color="primary"
                              class="text-none px-4"
                              @click="showOnlyPending = !showOnlyPending"
                            >
                              {{
                                showOnlyPending
                                  ? `Show ${
                                      competitors.length - filteredCompetitors.length
                                    } validated products`
                                  : 'Hide validated matches'
                              }}
                              <v-icon right small>
                                {{ showOnlyPending ? 'mdi-chevron-down' : 'mdi-chevron-up' }}
                              </v-icon>
                            </v-btn>
                          </td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>

                  <!-- ── Semantic Search view ── -->
                  <div v-else-if="activeView === 'semantic'">
                    <!-- Loading state (websites list fetching) -->
                    <div v-if="semanticWebsitesLoading && !semanticResultsByWebsite.length" class="d-flex flex-column align-center justify-center" style="height: 65vh">
                      <v-progress-circular indeterminate color="deep-purple" size="48" width="4" />
                      <div class="caption deep-purple--text mt-3 font-weight-medium">Running semantic search...</div>
                    </div>

                    <!-- Empty state -->
                    <div
                      v-else-if="!semanticResultsByWebsite.length"
                      class="d-flex flex-column align-center justify-center grey--text"
                      style="height: 65vh"
                    >
                      <v-icon size="48" color="grey lighten-1">mdi-magnify-close</v-icon>
                      <div class="text-subtitle-2 mt-2">No semantic matches found</div>
                      <div class="caption mt-1">Try lowering the minimum score threshold</div>
                    </div>

                    <!-- Results table -->
                    <v-simple-table v-else-if="semanticViewMode === 'table'" fixed-header height="65vh">
                      <template v-slot:default>
                        <thead>
                          <tr>
                            <th class="grey lighten-4 font-weight-medium text-caption" style="width:80px">Image</th>
                            <th class="grey lighten-4 font-weight-medium text-caption">Product</th>
                            <th class="grey lighten-4 font-weight-medium text-caption" style="width:160px">Price &amp; Stock</th>
                            <th class="text-center grey lighten-4 font-weight-medium text-caption" style="width:50px">#</th>
                            <th class="text-center grey lighten-4 font-weight-medium text-caption" style="width:56px">Add</th>
                          </tr>
                        </thead>
                        <tbody>
                          <!-- ── Selected / Pending save section ── -->
                          <template v-if="semanticPending.length">
                            <tr class="section-group-header">
                              <td colspan="5" class="px-3 py-2 green lighten-5">
                                <v-icon small color="success" class="mr-1">mdi-check-circle</v-icon>
                                <span class="caption font-weight-bold success--text">
                                  Selected to add / swap ({{ semanticPending.length }})
                                </span>
                                <span class="caption grey--text ml-2">— click a row to remove</span>
                              </td>
                            </tr>
                            <tr
                              v-for="(result, i) in semanticPending"
                              :key="'pending-' + i"
                              class="tabel-row pending-row"
                              @click="toggleSemanticMatch(result)"
                            >
                              <td class="pa-1">
                                <div class="w-100 d-flex justify-center" v-if="result.source.image">
                                  <img class="product-image" :src="result.source.image" />
                                </div>
                              </td>
                              <td class="pa-1">
                                <v-tooltip bottom>
                                  <template v-slot:activator="{ on, attrs }">
                                    <span v-bind="attrs" v-on="on" class="d-inline-block text-truncate caption font-weight-medium" style="max-width:100%">{{ result.source.name }}</span>
                                  </template>
                                  <span>{{ result.source.name }}</span>
                                </v-tooltip>
                                <div class="d-flex align-center mt-1" style="gap:4px">
                                  <img v-if="result.source.websiteAlias" :src="getWebsiteFavicon('https://' + result.source.websiteAlias)" height="12" />
                                  <a :href="result.source.url.startsWith('http') ? result.source.url : 'https://' + result.source.websiteAlias + result.source.url" target="_blank" class="caption grey--text text-decoration-none" style="font-size:11px" @click.stop>{{ result.source.websiteName || result.source.websiteAlias || '—' }}</a>
                                </div>
                                <div class="d-flex flex-wrap mt-1" style="gap:2px">
                                  <v-chip v-if="result.source.brandName" label x-small outlined>{{ result.source.brandName }}</v-chip>
                                  <v-chip v-if="result.source.ean" label x-small outlined>EAN: {{ result.source.ean }}</v-chip>
                                  <v-chip v-if="isValidExternalMatch(result)" x-small label color="warning" text-color="white"><v-icon x-small left>mdi-swap-horizontal</v-icon>Replacing existing</v-chip>
                                </div>
                              </td>
                              <td class="pa-1" style="white-space:nowrap">
                                <div class="d-flex align-center" style="gap:6px">
                                  <span v-if="result.source.offers && result.source.offers.length" class="caption font-weight-medium">{{ result.source.offers[0].price }}<span v-if="result.source.offers[0].currency" class="caption grey--text ml-1">{{ result.source.offers[0].currency }}</span></span>
                                  <span v-else class="grey--text caption">—</span>
                                  <badge v-if="result.source.offers && result.source.offers.length" class="sm-text" small="true" :text="result.source.offers[0].available ? 'InStock' : 'OutOfStock'" :color="result.source.offers[0].available ? 'success' : 'error'" />
                                </div>
                              </td>
                              <td class="text-center pa-1"><span class="rank-badge">{{ i + 1 }}</span></td>
                              <td class="text-center">
                                <v-tooltip right>
                                  <template v-slot:activator="{ on, attrs }">
                                    <v-btn v-bind="attrs" v-on="on" icon small @click.stop="toggleSemanticMatch(result)" class="pending-remove-btn">
                                      <v-icon class="pending-remove-btn__check" color="success" size="22">mdi-check-circle</v-icon>
                                      <v-icon class="pending-remove-btn__remove" color="error" size="22">mdi-close-circle</v-icon>
                                    </v-btn>
                                  </template>
                                  <span>Remove from selection</span>
                                </v-tooltip>
                              </td>
                            </tr>
                          </template>

                          <template v-if="semanticInMatch.length">
                            <tr class="section-group-header">
                              <td colspan="5" class="px-3 py-2 deep-purple lighten-5">
                                <v-icon small color="deep-purple" class="mr-1">mdi-link-variant</v-icon>
                                <span class="caption font-weight-bold deep-purple--text">
                                  Already in your match list ({{ semanticInMatch.length }})
                                </span>
                              </td>
                            </tr>
                            <tr
                              v-for="(result, i) in semanticInMatch"
                              :key="'in-' + i"
                              class="tabel-row exact-match-row"
                            >
                              <td class="pa-1">
                                <div class="w-100 d-flex justify-center" v-if="result.source.image">
                                  <img class="product-image" :src="result.source.image" />
                                </div>
                              </td>
                              <td class="pa-1">
                                <v-tooltip bottom>
                                  <template v-slot:activator="{ on, attrs }">
                                    <span v-bind="attrs" v-on="on" class="d-inline-block text-truncate caption font-weight-medium" style="max-width:100%">{{ result.source.name }}</span>
                                  </template>
                                  <span>{{ result.source.name }}</span>
                                </v-tooltip>
                                <div class="d-flex align-center mt-1" style="gap:4px">
                                  <img v-if="result.source.websiteAlias" :src="getWebsiteFavicon('https://' + result.source.websiteAlias)" height="12" />
                                  <a :href="result.source.url.startsWith('http') ? result.source.url : 'https://' + result.source.websiteAlias + result.source.url" target="_blank" class="caption grey--text text-decoration-none" style="font-size:11px" @click.stop>{{ result.source.websiteName || result.source.websiteAlias || '—' }}</a>
                                </div>
                                <div class="d-flex flex-wrap mt-1" style="gap:2px">
                                  <v-chip v-if="result.source.brandName" label x-small outlined>{{ result.source.brandName }}</v-chip>
                                  <v-chip v-if="result.source.ean" label x-small outlined>EAN: {{ result.source.ean }}</v-chip>
                                </div>
                              </td>
                              <td class="pa-1" style="white-space:nowrap">
                                <div class="d-flex align-center" style="gap:6px">
                                  <span v-if="result.source.offers && result.source.offers.length" class="caption font-weight-medium">{{ result.source.offers[0].price }}<span v-if="result.source.offers[0].currency" class="caption grey--text ml-1">{{ result.source.offers[0].currency }}</span></span>
                                  <span v-else class="grey--text caption">—</span>
                                  <badge v-if="result.source.offers && result.source.offers.length" class="sm-text" small="true" :text="result.source.offers[0].available ? 'InStock' : 'OutOfStock'" :color="result.source.offers[0].available ? 'success' : 'error'" />
                                </div>
                              </td>
                              <td class="text-center pa-1"><span class="rank-badge">{{ i + 1 }}</span></td>
                              <td class="text-center">
                                <v-tooltip right>
                                  <template v-slot:activator="{ on, attrs }">
                                    <v-btn v-bind="attrs" v-on="on" icon large disabled>
                                      <v-icon color="deep-purple lighten-2" size="28">mdi-link-variant</v-icon>
                                    </v-btn>
                                  </template>
                                  <span>Already in match list</span>
                                </v-tooltip>
                              </td>
                            </tr>
                          </template>

                          <!-- ── Suggestions section (per website) ── -->
                          <template v-for="(group, gi) in semanticSuggestionsByWebsite">
                            <template v-if="group.loading || group.allRows.length">
                              <tr class="section-group-header website-group-header" :key="'wg-head-' + gi" @click="toggleWebsiteCollapse(group.websiteAlias)">
                                <td colspan="5" class="px-2 py-1 grey lighten-3">
                                  <div class="d-flex align-center" style="gap: 8px">
                                    <v-btn icon x-small class="collapse-toggle-btn" :class="{ 'collapsed': semanticCollapsed[group.websiteAlias] }">
                                      <v-icon small>{{ semanticCollapsed[group.websiteAlias] ? 'mdi-chevron-right' : 'mdi-chevron-down' }}</v-icon>
                                    </v-btn>
                                    <img :src="getWebsiteFavicon('https://' + group.websiteAlias)" height="14" />
                                    <span class="caption font-weight-bold grey--text text--darken-3">{{ group.websiteAlias }}</span>
                                    <v-progress-circular v-if="group.loading" indeterminate size="12" width="2" color="grey darken-1" />
                                    <span v-else class="caption grey--text">({{ group.totalItems }})</span>
                                    <v-spacer />
                                    <v-text-field
                                      v-if="!semanticCollapsed[group.websiteAlias]"
                                      :value="semanticGroupSearch[group.websiteAlias] || ''"
                                      @input="$set(semanticGroupSearch, group.websiteAlias, $event)"
                                      @click.stop
                                      dense hide-details clearable solo flat
                                      placeholder="Filter results…"
                                      prepend-inner-icon="mdi-magnify"
                                      style="max-width:200px; font-size:12px"
                                      class="group-search-field"
                                    />
                                    <span v-else class="caption grey--text text--darken-1" style="font-size:10px">Click to expand</span>
                                  </div>
                                </td>
                              </tr>
                              <tr v-if="group.loading && !group.allRows.length && !semanticCollapsed[group.websiteAlias]" :key="'wg-load-' + gi">
                                <td colspan="5" class="pa-0">
                                  <v-progress-linear indeterminate color="deep-purple lighten-2" height="3" />
                                </td>
                              </tr>
                              <template v-if="!semanticCollapsed[group.websiteAlias]">
                                <template v-for="(row, ri) in group.allRows.filter(row => {
                                  const q = (semanticGroupSearch[group.websiteAlias] || '').toLowerCase().trim()
                                  if (!q || row.isHeader || row.isSeparator) return true
                                  const s = row.result.source
                                  return (s.name || '').toLowerCase().includes(q)
                                    || (s.ean || '').includes(q)
                                    || (s.brandName || '').toLowerCase().includes(q)
                                })">

                                  <!-- EAN section header -->
                                  <tr v-if="row.isHeader" :key="'wg-sh-' + gi + '-' + ri" class="section-group-header">
                                    <td colspan="5" class="px-3 py-1 amber lighten-5">
                                      <v-icon x-small color="amber darken-3" class="mr-1">mdi-barcode-scan</v-icon>
                                      <span class="caption font-weight-bold amber--text text--darken-3">EAN Matches ({{ row.count }})</span>
                                      <span class="caption grey--text ml-2">— closest EAN to your product</span>
                                    </td>
                                  </tr>
                                  <!-- Separator -->
                                  <tr v-else-if="row.isSeparator" :key="'wg-sep-' + gi + '-' + ri" class="section-group-header">
                                    <td colspan="5" class="px-3 py-1 grey lighten-5">
                                      <span class="caption grey--text">Other suggestions</span>
                                    </td>
                                  </tr>
                                  <!-- Data row -->
                                  <tr
                                    v-else
                                    :key="'wg-' + gi + '-' + ri"
                                    class="tabel-row"
                                    :class="{ 'in-current-matches-row': !!getExistingCompetitor(row.result), 'top-pick-row': row.section === 'top' }"
                                    @click="!getExistingCompetitor(row.result) && toggleSemanticMatch(row.result)"
                                  >
                                    <td class="pa-1">
                                      <div class="w-100 d-flex justify-center" v-if="row.result.source.image">
                                        <img class="product-image" :src="row.result.source.image" />
                                      </div>
                                    </td>
                                    <td class="pa-1">
                                      <v-tooltip bottom>
                                        <template v-slot:activator="{ on, attrs }">
                                          <span v-bind="attrs" v-on="on" class="d-inline-block text-truncate caption font-weight-medium" style="max-width:100%">{{ row.result.source.name }}</span>
                                        </template>
                                        <span>{{ row.result.source.name }}</span>
                                      </v-tooltip>
                                      <div class="d-flex align-center mt-1" style="gap:4px">
                                        <img v-if="row.result.source.websiteAlias" :src="getWebsiteFavicon('https://' + row.result.source.websiteAlias)" height="12" />
                                        <a :href="row.result.source.url.startsWith('http') ? row.result.source.url : 'https://' + row.result.source.websiteAlias + row.result.source.url" target="_blank" class="caption grey--text text-decoration-none" style="font-size:11px" @click.stop>{{ row.result.source.websiteName || row.result.source.websiteAlias || '—' }}</a>
                                      </div>
                                      <div class="d-flex flex-wrap mt-1" style="gap:2px">
                                        <v-chip v-if="row.result.source.brandName" label x-small outlined>{{ row.result.source.brandName }}</v-chip>
                                        <v-chip v-if="row.result.source.ean" label x-small outlined>EAN: {{ row.result.source.ean }}</v-chip>
                                        <v-chip v-if="row.section === 'top'" label x-small color="amber lighten-4">
                                          <v-icon x-small left color="amber darken-3">mdi-barcode-scan</v-icon>
                                          <span style="color:#e65100">{{ Math.round(row.eanScore * 100) }}% EAN</span>
                                        </v-chip>
                                        <v-chip v-if="isValidExternalMatch(row.result)" x-small label color="warning" text-color="white"><v-icon x-small left>mdi-swap-horizontal</v-icon>Has match</v-chip>
                                        <v-chip v-if="getExistingCompetitor(row.result)" x-small label color="primary" text-color="white"><v-icon x-small left>mdi-check-circle-outline</v-icon>In matches</v-chip>
                                      </div>
                                    </td>
                                    <td class="pa-1" style="white-space:nowrap">
                                      <div class="d-flex align-center" style="gap:6px">
                                        <span v-if="row.result.source.offers && row.result.source.offers.length" class="caption font-weight-medium">{{ row.result.source.offers[0].price }}<span v-if="row.result.source.offers[0].currency" class="caption grey--text ml-1">{{ row.result.source.offers[0].currency }}</span></span>
                                        <span v-else class="grey--text caption">—</span>
                                        <badge v-if="row.result.source.offers && row.result.source.offers.length" class="sm-text" small="true" :text="row.result.source.offers[0].available ? 'InStock' : 'OutOfStock'" :color="row.result.source.offers[0].available ? 'success' : 'error'" />
                                      </div>
                                    </td>
                                    <td class="text-center pa-1"><span class="rank-badge">{{ row.rowIndex }}</span></td>
                                    <td class="text-center">
                                      <template v-if="getExistingCompetitor(row.result)">
                                        <v-tooltip right>
                                          <template v-slot:activator="{ on, attrs }">
                                            <v-btn v-bind="attrs" v-on="on" icon large @click.stop="askDeleteFromSemantic(row.result)">
                                              <v-icon color="error" size="28">mdi-link-off</v-icon>
                                            </v-btn>
                                          </template>
                                          <span>Remove from current matches</span>
                                        </v-tooltip>
                                      </template>
                                      <template v-else>
                                        <v-tooltip right>
                                          <template v-slot:activator="{ on, attrs }">
                                            <v-btn v-bind="attrs" v-on="on" icon large @click.stop="toggleSemanticMatch(row.result)">
                                              <v-icon
                                                :color="isSemanticAdded(row.result) ? 'success' : isValidExternalMatch(row.result) ? 'warning' : 'grey lighten-1'"
                                                size="28"
                                              >
                                                {{
                                                  isSemanticAdded(row.result)
                                                    ? 'mdi-check-circle'
                                                    : isValidExternalMatch(row.result)
                                                    ? 'mdi-swap-horizontal-circle-outline'
                                                    : 'mdi-plus-circle-outline'
                                                }}
                                              </v-icon>
                                            </v-btn>
                                          </template>
                                          <span>{{
                                            isSemanticAdded(row.result)
                                              ? 'Remove'
                                              : isValidExternalMatch(row.result)
                                              ? 'Replace existing match'
                                              : 'Add as match'
                                          }}</span>
                                        </v-tooltip>
                                      </template>
                                    </td>
                                  </tr>
                                </template>
                              </template>
                            </template>
                          </template>
                        </tbody>
                      </template>
                    </v-simple-table>

                    <!-- ── Column view ── -->
                    <div v-else class="semantic-columns-container">
                      <div
                        v-for="(group, gi) in semanticSuggestionsByWebsite"
                        :key="'col-' + gi"
                        class="semantic-column"
                      >
                        <!-- Column header -->
                        <div class="semantic-column__header white">
                          <div class="d-flex align-center px-3 py-2" style="gap:8px; border-bottom:1px solid #e0e0e0">
                            <img :src="getWebsiteFavicon('https://' + group.websiteAlias)" height="16" style="flex-shrink:0" />
                            <span class="caption font-weight-bold grey--text text--darken-3 text-truncate" style="flex:1; min-width:0; font-size:12px">{{ group.websiteAlias }}</span>
                            <v-progress-circular v-if="group.loading" indeterminate size="12" width="2" color="grey darken-1" style="flex-shrink:0" />
                            <v-chip v-else x-small label :color="group.totalItems > 0 ? 'grey lighten-2' : 'grey lighten-3'" class="font-weight-medium" style="flex-shrink:0">{{ group.totalItems }}</v-chip>
                          </div>
                          <div v-if="group.totalItems > 0" class="px-2 py-1" style="background:#f5f5f5">
                            <v-text-field
                              :value="semanticGroupSearch[group.websiteAlias] || ''"
                              @input="$set(semanticGroupSearch, group.websiteAlias, $event)"
                              dense hide-details clearable solo flat
                              placeholder="Filter results…"
                              prepend-inner-icon="mdi-magnify"
                              style="font-size:12px; background:white; border-radius:4px"
                            />
                          </div>
                        </div>
                        <!-- Column body -->
                        <div class="semantic-column__body">
                          <v-progress-linear v-if="group.loading && !group.allRows.length" indeterminate color="grey lighten-2" height="2" />
                          <!-- Empty state -->
                          <div v-if="!group.loading && !group.allRows.length" class="d-flex flex-column align-center justify-center grey--text" style="height:100%; padding:24px">
                            <v-icon size="32" color="grey lighten-2">mdi-magnify-close</v-icon>
                            <span class="caption mt-2 text-center">No suggestions detected</span>
                          </div>
                          <template v-for="(row, ri) in group.allRows.filter(row => {
                            const q = (semanticGroupSearch[group.websiteAlias] || '').toLowerCase().trim()
                            if (!q || row.isHeader || row.isSeparator) return true
                            const s = row.result.source
                            return (s.name || '').toLowerCase().includes(q)
                              || (s.ean || '').includes(q)
                              || (s.brandName || '').toLowerCase().includes(q)
                          })">
                            <!-- EAN section header -->
                            <div v-if="row.isHeader" :key="'ch-' + gi + '-' + ri" class="px-3 py-2 amber lighten-5 d-flex align-center" style="border-bottom:1px solid #ffe082; border-top:1px solid #ffe082">
                              <v-icon x-small color="amber darken-3" class="mr-1">mdi-barcode-scan</v-icon>
                              <span class="caption font-weight-bold amber--text text--darken-3">EAN Matches</span>
                              <v-chip x-small label color="amber darken-2" dark class="ml-2">{{ row.count }}</v-chip>
                            </div>
                            <!-- Separator -->
                            <div v-else-if="row.isSeparator" :key="'cs-' + gi + '-' + ri" class="px-3 py-2 d-flex align-center" style="border-bottom:1px solid #e0e0e0; border-top:1px solid #e0e0e0; background:#f5f5f5">
                              <span class="caption grey--text text--darken-1 font-weight-medium" style="font-size:10px; text-transform:uppercase; letter-spacing:.5px">Other suggestions</span>
                            </div>
                            <!-- Result card -->
                            <div
                              v-else
                              :key="'cc-' + gi + '-' + ri"
                              class="semantic-col-card"
                              :class="{
                                'in-current-col-card': !!getExistingCompetitor(row.result),
                                'top-pick-col-card': row.section === 'top'
                              }"
                              @click="!getExistingCompetitor(row.result) && toggleSemanticMatch(row.result)"
                            >
                              <!-- Top row: image + name/brand/ean + rank -->
                              <div class="d-flex" style="gap:10px">
                                <div class="semantic-col-card__img">
                                  <img v-if="row.result.source.image" :src="row.result.source.image" style="width:100%; height:100%; object-fit:contain" />
                                  <v-icon v-else color="grey lighten-2" size="28">mdi-image-off-outline</v-icon>
                                </div>
                                <div style="flex:1; min-width:0; display:flex; flex-direction:column; gap:3px">
                                  <div class="caption font-weight-medium" style="line-height:1.35; display:-webkit-box; -webkit-line-clamp:2; line-clamp:2; -webkit-box-orient:vertical; overflow:hidden">{{ row.result.source.name }}</div>
                                  <div v-if="row.result.source.brandName" class="grey--text" style="font-size:10px">{{ row.result.source.brandName }}</div>
                                  <!-- EAN + similarity -->
                                  <div class="d-flex align-center flex-wrap" style="gap:4px; margin-top:2px">
                                    <v-chip v-if="row.result.source.ean" label x-small outlined style="font-size:10px; height:16px">
                                      <v-icon x-small left color="grey darken-1">mdi-barcode</v-icon>{{ row.result.source.ean }}
                                    </v-chip>
                                    <v-chip v-if="row.section === 'top'" label x-small color="amber lighten-4" style="font-size:10px; height:16px">
                                      <v-icon x-small left color="amber darken-3">mdi-barcode-scan</v-icon>
                                      <span style="color:#e65100; font-weight:600">{{ Math.round(row.eanScore * 100) }}%</span>
                                    </v-chip>
                                  </div>
                                </div>
                                <span class="rank-badge" style="flex-shrink:0; align-self:flex-start">{{ row.rowIndex }}</span>
                              </div>

                              <!-- Price + stock row -->
                              <div class="semantic-col-card__grid mt-2">
                                <div class="semantic-col-card__cell">
                                  <span v-if="row.result.source.offers && row.result.source.offers.length" class="font-weight-bold" style="font-size:13px; color:#212121">{{ formatSemanticPrice(row.result.source.offers[0].price) }}</span>
                                  <span v-else class="grey--text caption">—</span>
                                </div>
                                <div class="semantic-col-card__cell semantic-col-card__cell--right">
                                  <badge v-if="row.result.source.offers && row.result.source.offers.length" class="sm-text" small="true" :text="row.result.source.offers[0].available ? 'InStock' : 'OutOfStock'" :color="row.result.source.offers[0].available ? 'success' : 'error'" />
                                </div>
                              </div>

                              <!-- Footer: status chips + action -->
                              <div class="d-flex align-center mt-2" style="gap:4px">
                                <v-chip v-if="getExistingCompetitor(row.result)" x-small label color="primary" text-color="white" style="font-size:10px">
                                  <v-icon x-small left>mdi-check-circle-outline</v-icon>In matches
                                </v-chip>
                                <v-chip v-else-if="isValidExternalMatch(row.result)" x-small label color="warning" text-color="white" style="font-size:10px">
                                  <v-icon x-small left>mdi-swap-horizontal</v-icon>Has match
                                </v-chip>
                                <v-spacer />
                                <v-btn
                                  icon small
                                  @click.stop="getExistingCompetitor(row.result) ? askDeleteFromSemantic(row.result) : toggleSemanticMatch(row.result)"
                                >
                                  <v-icon
                                    :color="getExistingCompetitor(row.result) ? 'error' : isSemanticAdded(row.result) ? 'success' : isValidExternalMatch(row.result) ? 'warning' : 'grey lighten-1'"
                                    size="22"
                                  >
                                    {{
                                      getExistingCompetitor(row.result)
                                        ? 'mdi-link-off'
                                        : isSemanticAdded(row.result)
                                        ? 'mdi-check-circle'
                                        : isValidExternalMatch(row.result)
                                        ? 'mdi-swap-horizontal-circle-outline'
                                        : 'mdi-plus-circle-outline'
                                    }}
                                  </v-icon>
                                </v-btn>
                              </div>
                            </div>
                          </template>
                        </div>
                      </div>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </template>
          </div>
        </v-container>
      </v-card-text>

      <!-- Sticky Action Bar -->
      <v-card-actions class="sticky-bottom white px-5 py-3">
        <v-btn text color="error" @click="dialog = false" class="text-none nav-action-btn" small>
          <v-icon left small>mdi-close</v-icon>
          Cancel
        </v-btn>

        <v-spacer></v-spacer>

        <template v-if="!confirm">
          <!-- Navigation group -->
          <div class="nav-group mr-4">
            <button class="nav-group__btn nav-group__btn--prev" :disabled="loading || submit_loading" @click="previousProduct()">
              <v-icon size="18">mdi-chevron-left</v-icon>
              <span>Prev</span>
            </button>

            <div class="nav-group__counter">
              <span class="nav-group__pos">{{ productPosition + 1 }}</span>
              <span class="nav-group__sep">/</span>
              <span class="nav-group__total">{{ matches.length }}</span>
            </div>

            <div class="nav-group__page-badge" v-if="searchParams.page">
              Page {{ searchParams.page }}
            </div>

            <button class="nav-group__btn nav-group__btn--next" :disabled="loading || submit_loading" @click="nextProduct()">
              <span>Next</span>
              <v-icon size="18">mdi-chevron-right</v-icon>
            </button>
          </div>

          <v-btn color="primary" depressed @click="next" class="px-5 text-none" small elevation="0">
            <v-icon left small>mdi-check-all</v-icon>
            Review & Save
          </v-btn>
        </template>

        <template v-else>
          <div class="d-flex align-center">
            <span class="caption grey--text mr-3 hidden-sm-and-down">Confirm changes?</span>
            <v-btn
              @click="confirmDeleteProducts(false)"
              color="primary"
              outlined
              small
              class="mr-2 text-none"
              width="140"
            >
              <v-icon left small>mdi-content-save-outline</v-icon>
              Save Only
            </v-btn>
            <v-btn
              @click="confirmDeleteProducts(true)"
              color="primary"
              depressed
              small
              class="text-none"
              width="140"
              elevation="2"
            >
              <v-icon left small>mdi-check-circle-outline</v-icon>
              Save & Validate
            </v-btn>
          </div>
        </template>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
  import axios from 'axios'
  export default {
    name: 'validateMatchModal',
    components: {},
    props: ['productToEdit', 'show', 'component_name', 'matchesList', 'pagination', 'filter'],
    data() {
      return {
        ProductToEdit: {},
        searchParams: {},
        defaultProduct: {},
        dialog: false,
        loading: false,
        competitors: [],
        selectedProductsToDelete: false,
        incrSelectionAction: 0,
        productsToDelete: [],
        submit_loading: false,
        confirm: false,
        verified: false,
        matches: [],
        productPosition: 0,
        initialList: true,
        showOnlyPending:
          localStorage.getItem('validateMatchModal_showOnlyPending') !== null
            ? localStorage.getItem('validateMatchModal_showOnlyPending') === 'true'
            : true,
        activeView: 'current',
        semanticResultsByWebsite: [],
        semanticSearchId: 0,
        semanticAdded: [],
        semanticMinScore: localStorage.getItem('semanticMinScore') !== null
          ? parseFloat(localStorage.getItem('semanticMinScore'))
          : null,
        semanticQuery: '',
        semanticWebsites: [],
        semanticWebsitesLoading: false,
        semanticWebsiteIdMap: {},
        semanticSelectedWebsites: [],
        semanticCollapsed: {},
        semanticGroupSearch: {},
        semanticViewMode: localStorage.getItem('semanticViewMode') || 'columns',
        semanticPriceRange: JSON.parse(localStorage.getItem('semanticPriceRange') || 'null') || [-100, 100],
        semanticPriceMenuOpen: false,
        swapConfirmDialog: false,
        swapConfirmResult: null,
        addConfirmDialog: false,
        addConfirmResult: null,
        deleteCurrentConfirmDialog: false,
        deleteCurrentResult: null,
        deleteFromSemanticConfirmDialog: false,
        deleteFromSemanticResult: null,
      }
    },
    computed: {
      filteredCompetitors() {
        if (this.showOnlyPending) {
          return this.competitors.filter((c) => c.product.delete)
        }
        return this.competitors
      },
      semanticResults() {
        return this.semanticResultsByWebsite.flatMap((w) => w.items)
      },
      semanticLoading() {
        return this.semanticResultsByWebsite.some((w) => w.loading)
      },
      semanticRefPrice() {
        const offers = this.defaultProduct?.product?.offers
        return offers && offers.length ? parseFloat(offers[0].price) : null
      },
      semanticPriceFilterActive() {
        return this.semanticPriceRange[0] !== -100 || this.semanticPriceRange[1] !== 100
      },
      semanticSuggestionsByWebsite() {
        const refEan = this.defaultProduct?.product?.ean
        return this.semanticResultsByWebsite.map((w) => {
          const available = w.items.filter(
            (r) => !this.isAlreadyInMatchList(r) && !this.isSemanticAdded(r),
          )

          // Score each result by EAN similarity if reference EAN exists
          const topPickUrls = new Set()
          if (refEan) {
            available
              .map((r) => ({ url: r.source.url, score: this.eanSimilarity(refEan, r.source?.ean) }))
              .filter((x) => x.score >= 0.6)
              .sort((a, b) => b.score - a.score)
              .slice(0, 5)
              .forEach((x) => topPickUrls.add(x.url))
          }

          const topPicks = available
            .filter((r) => topPickUrls.has(r.source.url))
            .map((r) => ({ result: r, eanScore: this.eanSimilarity(refEan, r.source?.ean), section: 'top' }))
            .sort((a, b) => b.eanScore - a.eanScore)
          const regular = available
            .filter((r) => !topPickUrls.has(r.source.url))
            .map((r) => ({ result: r, eanScore: 0, section: 'regular' }))

          // Build flat allRows array with marker rows for section headers
          const allRows = []
          if (topPicks.length) {
            allRows.push({ isHeader: true, label: 'EAN Matches', count: topPicks.length })
            topPicks.forEach((r, idx) => allRows.push({ ...r, rowIndex: idx + 1 }))
            if (regular.length) allRows.push({ isSeparator: true })
          }
          regular.forEach((r, idx) => allRows.push({ ...r, rowIndex: idx + 1 }))

          return {
            websiteAlias: w.websiteAlias,
            loading: w.loading,
            totalItems: available.length,
            allRows,
            get filteredRows() {
              return allRows
            },
          }
        })
      },
      semanticInMatch() {
        return this.semanticResults.filter((r) => this.isAlreadyInMatchList(r))
      },
      semanticPending() {
        return this.semanticResults.filter((r) => this.isSemanticAdded(r))
      },
      semanticSuggestions() {
        return this.semanticResults.filter(
          (r) => !this.isAlreadyInMatchList(r) && !this.isSemanticAdded(r),
        )
      },
      swapExistingProduct() {
        if (!this.swapConfirmResult) return null
        const alias = this.swapConfirmResult.source.websiteAlias
        const entry = this.matchesList[this.productPosition]
        return entry && alias ? entry[alias] : null
      },
    },
    mounted() {},
    methods: {
      showEditModal(item) {
        this.$emit('show-edit-modal', item)
      },
      eanSimilarity(ean1, ean2) {
        if (!ean1 || !ean2) return 0
        // Normalise: strip non-digits, pad to 13 with leading zeros
        const a = String(ean1).replace(/\D/g, '').padStart(13, '0')
        const b = String(ean2).replace(/\D/g, '').padStart(13, '0')
        if (a === b) return 1.0
        let prefix = 0
        for (let i = 0; i < 13; i++) {
          if (a[i] === b[i]) prefix++
          else break
        }
        return prefix / 13
      },
      toggleWebsiteCollapse(alias) {
        this.$set(this.semanticCollapsed, alias, !this.semanticCollapsed[alias])
      },
      formatSemanticPrice(price) {
        if (price === undefined || price === null) return null
        try {
          const country = JSON.parse(localStorage.user_website).country
          const localeMap = {
            mx: ['es-MX', 'MXN'],
            es: ['es-ES', 'EUR'],
            ch: ['de-CH', 'CHF'],
            uk: ['en-GB', 'GBP'],
            do: ['es-DO', 'DOP'],
          }
          const [locale, currency] = localeMap[country] || ['es-ES', 'EUR']
          return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(price)
        } catch {
          return price
        }
      },
      resetSemanticSearch() {
        this.semanticQuery = this.defaultProduct?.product?.name || ''
        this.semanticMinScore = null
        this.semanticSelectedWebsites = []
        this.semanticPriceRange = [-100, 100]
        localStorage.removeItem('semanticMinScore')
        localStorage.removeItem('semanticPriceRange')
        this.fetchSemanticSimilarities()
      },
      async fetchSemanticWebsites() {
        this.semanticWebsitesLoading = true
        try {
          const res = await axios.get('/api/products/similarsearch/websites', {
            params: { index: localStorage.getItem('account_id') + '_competitors_similarities' },
          })
          this.semanticWebsites = res.data || []
          // Build alias → id map from the user_websites stored in localStorage at login
          const allWebsites = JSON.parse(localStorage.getItem('user_websites') || '[]')
          this.semanticWebsiteIdMap = allWebsites.reduce((map, w) => {
            if (w.alias && w.id != null) map[w.alias] = w.id
            return map
          }, {})
        } catch (err) {
          console.error('Error fetching semantic websites:', err)
          this.semanticWebsites = []
        } finally {
          this.semanticWebsitesLoading = false
        }
      },
      async fetchSemanticSimilarities() {
        const query = (this.semanticQuery || '').trim() || this.defaultProduct?.product?.name
        if (!query) return

        const websitesToQuery = this.semanticSelectedWebsites.length
          ? this.semanticSelectedWebsites
          : this.semanticWebsites

        if (!websitesToQuery.length) return

        const index = localStorage.getItem('account_id') + '_competitors_similarities'
        // Bump search ID so stale responses from a previous call are ignored
        const searchId = ++this.semanticSearchId

        // Initialize per-website entries in the specified order
        this.semanticResultsByWebsite = websitesToQuery.map((w) => ({
          websiteAlias: w,
          loading: true,
          items: [],
        }))

        // Fire one independent request per website
        websitesToQuery.forEach((websiteAlias, idx) => {
          axios
            .get('/api/products/similarsearch', {
              params: {
                query,
                index,
                topK: 100,
                ...(this.semanticMinScore !== null && this.semanticMinScore !== undefined && { minScore: this.semanticMinScore }),
                ...(this.semanticPriceFilterActive && this.semanticRefPrice && {
                  minPrice: this.semanticRefPrice * (1 + this.semanticPriceRange[0] / 100),
                  maxPrice: this.semanticRefPrice * (1 + this.semanticPriceRange[1] / 100),
                }),
                websiteAlias,
              },
            })
            .then((res) => {
              if (searchId !== this.semanticSearchId) return
              this.$set(this.semanticResultsByWebsite, idx, {
                ...this.semanticResultsByWebsite[idx],
                items: res.data || [],
              })
            })
            .catch((err) => {
              if (searchId !== this.semanticSearchId) return
              console.error(`Semantic search error for ${websiteAlias}:`, err)
              this.$set(this.semanticResultsByWebsite, idx, {
                ...this.semanticResultsByWebsite[idx],
                items: [],
              })
            })
            .finally(() => {
              if (searchId !== this.semanticSearchId) return
              this.$set(this.semanticResultsByWebsite, idx, {
                ...this.semanticResultsByWebsite[idx],
                loading: false,
              })
            })
        })
      },
      toggleSemanticMatch(result) {
        const idx = this.semanticAdded.findIndex((r) => r.source.url === result.source.url)
        if (idx !== -1) {
          // Already selected — deselect immediately
          this.semanticAdded.splice(idx, 1)
          return
        }
        if (this.isValidExternalMatch(result)) {
          // Has an existing match — ask for swap confirmation first
          this.swapConfirmResult = result
          this.swapConfirmDialog = true
        } else {
          // No existing match — ask for add confirmation
          this.addConfirmResult = result
          this.addConfirmDialog = true
        }
      },
      confirmAdd() {
        if (this.addConfirmResult) {
          const sameWebsiteIdx = this.semanticAdded.findIndex(
            (r) => r.source.websiteAlias === this.addConfirmResult.source.websiteAlias,
          )
          if (sameWebsiteIdx !== -1) this.semanticAdded.splice(sameWebsiteIdx, 1)
          this.semanticAdded.push(this.addConfirmResult)
        }
        this.addConfirmDialog = false
        this.addConfirmResult = null
      },
      confirmSwap() {
        if (this.swapConfirmResult) {
          // Remove any existing entry from the same website first
          const sameWebsiteIdx = this.semanticAdded.findIndex(
            (r) => r.source.websiteAlias === this.swapConfirmResult.source.websiteAlias,
          )
          if (sameWebsiteIdx !== -1) this.semanticAdded.splice(sameWebsiteIdx, 1)
          this.semanticAdded.push(this.swapConfirmResult)
        }
        this.swapConfirmDialog = false
        this.swapConfirmResult = null
      },
      getExistingCompetitor(result) {
        return this.competitors.find((c) => c.website.id === result.source.websiteId) || null
      },
      isAlreadyInMatchList(result) {
        const products = this.matchesList[this.productPosition]
        console.log('products in match list  :', products);
        if (!products) return false

        if(!products[result.source.websiteAlias]) return false;

        console.log(Object.keys(products))

        const product_url = products[result.source.websiteAlias].product.url;
        const similarity_product_url = result.source.url;

        console.log('result : ',result);
        console.log('product url :',product_url);
        console.log('similarity product url :',similarity_product_url);


        const isAlreadyMatchedProduct = product_url.toLowerCase().includes(similarity_product_url.toLowerCase())

        if(isAlreadyMatchedProduct){
          console.log('yes, the product url is included in the similarity product url : ', product_url, similarity_product_url);
          //alert('Yes, the product url is included in the similarity product url. This means that the product is already in the match list, even if the URLs are not exactly the same. We consider it a match because the base URL is the same, which indicates that it is likely the same product listed on the same website.')
          return true
        }

        return false;
        
      },
      isValidExternalMatch(result) {
        //products
        const products = this.matchesList[this.productPosition]
        const websiteAlias = result.source.websiteAlias || ''
        if(websiteAlias){
          const matchedProduct = products[websiteAlias] || null;
          if(matchedProduct){
            return true
          }
        }
        return false
      },
      askDeleteProduct(competitor) {
        if (competitor.product.delete) {
          // Already marked — unmark immediately without confirmation
          this.deleteProduct(competitor.product)
        } else {
          this.deleteCurrentResult = competitor
          this.deleteCurrentConfirmDialog = true
        }
      },
      confirmDeleteCurrent() {
        if (this.deleteCurrentResult) {
          this.deleteProduct(this.deleteCurrentResult.product)
        }
        this.deleteCurrentConfirmDialog = false
        this.deleteCurrentResult = null
      },
      askDeleteFromSemantic(result) {
        this.deleteFromSemanticResult = result
        this.deleteFromSemanticConfirmDialog = true
      },
      confirmDeleteFromSemantic() {
        const competitor = this.getExistingCompetitor(this.deleteFromSemanticResult)
        if (competitor) {
          // Mark it for deletion using the existing mechanism
          if (!competitor.product.delete) {
            this.deleteProduct(competitor.product)
          }
        }
        this.deleteFromSemanticConfirmDialog = false
        this.deleteFromSemanticResult = null
      },
      isSemanticAdded(result) {
        return this.semanticAdded.some((r) => r.source.url === result.source.url)
      },
      deleteProduct(item) {
        this.incrSelectionAction++
        item.delete = !item.delete
        this.selectedProductsToDelete = this.competitors.filter(
          (competitor) => competitor.product.delete,
        ).length
          ? true
          : false
      },
      next() {
        this.confirm = true
      },
      submitChanges() {
        const productsToDelete = this.competitors.filter((competitor) => competitor.product.delete)

        //update to validated
        const toUpdate = this.competitors
          .filter((competitor) => !competitor.product.delete)
          .map((p) => {
            if (!p.product.validated) {
              p.validatedUpdated = true
              p.product.validated = true
            }
            return p
          })
          .filter((p) => p.validatedUpdated)

        this.submit_loading = true

        const payload = {
          data: {
            match_id: this.ProductToEdit.match_id,
            match_ref: this.ProductToEdit.match_ref,
            verified: this.verified,
            account: localStorage.getItem('account_id'),
            user: localStorage.getItem('user_id'),
            component: this.component_name,
            to_delete: [
              ...productsToDelete.map((product) => ({
                id: product.product.id,
                ref: this.ProductToEdit.match_ref,
                url: product.product.url,
                website: product.website.id,
                website_url: product.website.url,
              })),
              ...this.semanticAdded
                .filter((result) => this.isValidExternalMatch(result))
                .map((result) => {
                  const competitor = this.competitors.find(
                    (c) =>
                      c.website.id === result.source.websiteId ||
                      c.website.alias === result.source.websiteAlias,
                  )
                  if (!competitor) return null
                  return {
                    id: competitor.product.id,
                    ref: this.ProductToEdit.match_ref,
                    url: competitor.product.url,
                    website: competitor.website.id,
                    website_url: competitor.website.url,
                  }
                })
                .filter(
                  (item, idx, arr) =>
                    item &&
                    !productsToDelete.some((p) => p.product.id === item.id) &&
                    arr.findIndex((x) => x && x.id === item.id) === idx,
                ),
            ],
            to_add: this.semanticAdded.map((result) => {
              // Resolve websiteId: ES source field → current competitors → localStorage user_websites map
              const websiteId =
                result.source.websiteId ??
                this.competitors.find((c) => c.website.alias === result.source.websiteAlias)?.website?.id ??
                this.semanticWebsiteIdMap[result.source.websiteAlias] ??
                null
              if (websiteId == null) {
                console.warn('[to_add] missing websiteId for', result.source.websiteAlias, result.source)
              }
              return {
                id: result.source.id || '',
                ref: this.ProductToEdit.match_ref,
                url: result.source.url.startsWith('http') ? result.source.url : 'https://' + result.source.websiteAlias + result.source.url,
                website: websiteId,
                website_url: 'https://' + result.source.websiteAlias,
              }
            }),
            to_update: toUpdate.map((product) => {
              return {
                id: product.product.id,
                ref: this.ProductToEdit.match_ref,
                url: product.product.url,
                website: product.website.id,
                url_website: product.website.url,
                website_url: product.website.url,
                validated: product.product.validated,
              }
            }),
            initialList: true,
          },
        }

        //alert(payload);
        console.log(JSON.stringify(payload,null,2));


        axios
          .post('/api/products/editMatch', payload)
          .catch(() => {
            this.$emit('show-notification', {
              msg: `An error occured while processing your request, please try again.`,
              type: 'error',
            })
          })
          .finally(() => {
            this.$emit('callback-function')
            this.submit_loading = false
            this.competitors = []
            this.productsToDelete = []
            this.activeView = 'current'
            //this.dialog = false
            this.nextProduct().then(() => {
              this.$emit('show-notification', {
                msg: `The change that you have made, has been well processed`,
                type: 'success',
              })
            })
          })
      },
      confirmDeleteProducts(verified = true) {
        this.confirm = false
        this.verified = verified
        this.submitChanges()
      },
      setMatchesList(data = []) {
        if (data.length) {
          //console.log('data set matches list ', data)
          
          console.log('init data : ',data);

          //data = data.filter((match) => !match.verified)

          this.ProductToEdit = data[this.productPosition]
          return data
        } else {
          if (this.productPosition == 0) {
            if (this.productToEdit.match_id !== this.matchesList[0].match_id) {
              this.productPosition = this.matchesList.findIndex(
                (match) => match.match_id === this.productToEdit.match_id,
              )
            }
          }

          this.ProductToEdit = (this.matches.length ? this.matches : this.matchesList)[
            this.productPosition
          ]

          return this.matches.length ? this.matches : this.matchesList
        }
      },
      async nextProduct() {
        
        console.log('matches.length : ',this.matches.length);

        if (this.productPosition < this.matches.length - 1) {
          this.productPosition++
          this.initialisation(this.productPosition, Object.assign({}, this.searchParams))
        } else {
          //if reached the end of the list, let's use searchParams to get the next list from server
          this.productPosition = 0
          this.searchParams.page++
          this.searchParams.from =
            this.searchParams.page * this.searchParams.rowsPerPage - this.searchParams.rowsPerPage
          this.searchParams.size = this.searchParams.rowsPerPage
          const response = await this.getData()


          console.log('current page: ', this.searchParams.page);
          console.log('from : ', this.searchParams.from);

          this.initialisation(
            this.productPosition,
            Object.assign({}, this.searchParams),
            response.data.data,
          )
        }
      },
      previousProduct() {
        if (this.productPosition > 0) this.productPosition--
        else this.productPosition = this.matches.length - 1
        this.initialisation(this.productPosition, this.filter)
      },
      initialisation(productPosition = 0, filter = {}, data = []) {
        this.competitors = []
        this.productPosition = productPosition
        this.matches = Object.assign([], this.setMatchesList(data))

        Object.assign(this.searchParams, filter)

        console.log('product to edit : ',this.ProductToEdit);

        Object.keys(this.ProductToEdit).forEach((key) => {
          if (this.ProductToEdit[key] && this.ProductToEdit[key].product) {
            this.ProductToEdit[key].product.match_id = this.ProductToEdit.match_id
            this.ProductToEdit[key].product.match_ref = this.ProductToEdit.match_ref
            this.ProductToEdit[key].product.delete = !this.ProductToEdit[key].product.validated

            if (this.ProductToEdit[key].website.alias == localStorage.getItem('website_alias'))
              this.competitors.unshift(this.ProductToEdit[key])
            else this.competitors.push(this.ProductToEdit[key])
          }
        })
        if (this.competitors.length) this.defaultProduct = this.competitors[0]
        if (this.defaultProduct.product.id)
          this.competitors = this.competitors
            .filter((competitor) => competitor.product.id !== this.defaultProduct.product.id)
            .sort((a, b) => {
              // Sort by delete status (true/unvalidated first) then by confidence
              if (a.product.delete !== b.product.delete) {
                return b.product.delete ? 1 : -1
              }
              return b.product.meta.matching_confidence - a.product.meta.matching_confidence
            })

        // Re-fetch semantic similarities for the new product if the tab is active
        this.semanticResultsByWebsite = []
        this.semanticAdded = []
        this.semanticQuery = this.defaultProduct?.product?.name || ''
        this.semanticSelectedWebsites = []
        this.semanticWebsites = []
        this.semanticWebsiteIdMap = {}
        if (this.activeView === 'semantic') {
          this.fetchSemanticWebsites().then(() => this.fetchSemanticSimilarities())
        }
      },
      async getData() {
        this.loading = true

        return axios
          .get('/api/products/list', {
            params: {
              ...this.searchParams,
              updateInterval: localStorage.getItem('updateInterval') || 1,
            },
          })
          .then((res) => {
            return res
          })
          .catch((err) => {
            console.error('comparePricesTable - getProducts: ', err)
            return []
          })
          .finally(() => {
            this.loading = false
          })
      },
      async modalOpened() {
        this.matches = []
        this.productPosition = 0
        this.initialisation(this.productPosition, this.filter)
        this.confirm = false
        this.activeView = 'current'
      },
    },
    watch: {
      showOnlyPending(val) {
        localStorage.setItem('validateMatchModal_showOnlyPending', val)
      },
      async activeView(val) {
        if (val === 'semantic') {
          if (!this.semanticWebsites.length) {
            await this.fetchSemanticWebsites()
          }
          if (!this.semanticResultsByWebsite.length) {
            this.fetchSemanticSimilarities()
          }
        }
      },
      semanticMinScore(val) {
        if (val !== null && val !== undefined) {
          localStorage.setItem('semanticMinScore', val)
        } else {
          localStorage.removeItem('semanticMinScore')
        }
        if (this.activeView === 'semantic') {
          this.fetchSemanticSimilarities()
        }
      },
      semanticPriceRange(val) {
        localStorage.setItem('semanticPriceRange', JSON.stringify(val))
      },
      dialog: {
        handler(value) {
          if (!value) {
            this.competitors = []
            this.defaultProduct = {}
          }
        },
      },
      show: {
        handler() {
          this.dialog = true
          this.modalOpened()
          this.to_add = []
          this.to_update = []
        },
      },
    },
  }
</script>
<style scoped>
  /* Modal Card */
  .validate-modal-card {
    border-radius: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    height: 100vh;
  }

  /* Reference Product Section */
  .reference-section {
    border-radius: 8px;
    overflow: visible;
  }

  .reference-image-wrapper {
    width: 96px;
    height: 96px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f5f7fa;
    border-radius: 8px;
    padding: 6px;
    flex-shrink: 0;
    overflow: hidden;
  }

  .reference-product-image {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }

  /* Competitors Section */
  .competitors-section {
    border-radius: 8px;
    overflow: hidden;
  }

  /* Rank badge */
  .rank-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 28px;
    height: 22px;
    padding: 0 5px;
    border-radius: 11px;
    background-color: #eeeeee;
    color: #616161;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.3px;
  }
  .rank-badge::before {
    content: '#';
    font-size: 9px;
    margin-right: 1px;
    opacity: 0.7;
  }

  /* Website group collapse header */
  .website-group-header {
    cursor: pointer;
    user-select: none;
  }
  .website-group-header:hover td {
    background-color: #e0e0e0 !important;
  }
  .collapse-toggle-btn {
    background-color: #fff !important;
    border: 1px solid #bdbdbd !important;
    border-radius: 4px !important;
    transition: background-color 0.15s ease;
  }
  .website-group-header:hover .collapse-toggle-btn {
    background-color: #f5f5f5 !important;
    border-color: #9e9e9e !important;
  }

  /* Top pick rows (EAN match) */
  .top-pick-row {
    background-color: #fffde7;
  }
  .top-pick-row:hover {
    background-color: #fff9c4 !important;
  }

  /* Column layout */
  .semantic-columns-container {
    display: flex;
    flex-direction: row;
    height: 65vh;
    overflow-x: auto;
    background: #f0f0f0;
    gap: 1px;
  }
  .semantic-column {
    flex: 1 1 0%;
    min-width: 200px;
    display: flex;
    flex-direction: column;
    height: 100%;
    background: white;
    overflow: hidden;
  }
  .semantic-column__header {
    flex-shrink: 0;
  }
  .semantic-column__body {
    flex: 1;
    overflow-y: auto;
    background: #fafafa;
  }

  /* Column cards */
  .semantic-col-card {
    padding: 10px 12px;
    border-bottom: 1px solid #eeeeee;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    gap: 0;
    background: white;
    transition: background 0.1s;
  }
  .semantic-col-card:hover {
    background: #f5f7ff;
  }
  .semantic-col-card__img {
    width: 52px;
    height: 52px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    background: #f5f5f5;
    overflow: hidden;
  }
  .semantic-col-card__grid {
    display: grid;
    grid-template-columns: 1fr auto;
    row-gap: 5px;
    column-gap: 8px;
    align-items: center;
    padding-top: 8px;
    border-top: 1px solid #f0f0f0;
    margin-top: 8px;
  }
  .semantic-col-card__cell {
    display: flex;
    align-items: center;
    min-width: 0;
    overflow: hidden;
  }
  .semantic-col-card__cell--right {
    justify-content: flex-end;
    flex-shrink: 0;
  }
  .top-pick-col-card {
    background: #fffde7 !important;
  }
  .top-pick-col-card:hover {
    background: #fff9c4 !important;
  }
  .in-current-col-card {
    background: #e8f5e9 !important;
    cursor: default !important;
  }
  .in-current-col-card:hover {
    background: #dcedc8 !important;
  }

  /* Beta badge */
  .beta-badge {
    display: inline-block;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.5px;
    line-height: 1;
    padding: 2px 4px;
    border-radius: 3px;
    background: linear-gradient(135deg, #7c4dff, #448aff);
    color: #fff;
    vertical-align: middle;
    text-transform: uppercase;
  }

  /* Table rows */
  .tabel-row {
    height: 64px;
    transition: background-color 0.2s ease;
  }

  .tabel-row:hover {
    background-color: #fafafa;
  }

  .product-image {
    object-fit: contain;
    max-height: 48px;
    max-width: 48px;
    height: 48px;
    background-size: cover;
    border-radius: 4px;
  }

  .more-info {
    cursor: pointer;
  }

  /* Favicon */
  .favicon-img {
    border-radius: 4px;
  }

  /* Navigation pill group */
  .nav-group {
    display: inline-flex;
    align-items: stretch;
    border: 1px solid #d0d5dd;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 1px 2px rgba(0,0,0,0.06);
    height: 32px;
  }

  .nav-group__btn {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    padding: 0 12px;
    background: #fff;
    border: none;
    cursor: pointer;
    font-size: 13px;
    font-weight: 500;
    color: #344054;
    transition: background 0.15s;
    outline: none;
    line-height: 1;
  }

  .nav-group__btn:hover {
    background: #f5f5f5;
  }

  .nav-group__btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    pointer-events: none;
  }

  .nav-group__btn--prev {
    border-right: 1px solid #d0d5dd;
  }

  .nav-group__btn--next {
    border-left: 1px solid #d0d5dd;
  }

  .nav-group__counter {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    padding: 0 14px;
    background: #f9fafb;
    font-size: 13px;
    user-select: none;
    white-space: nowrap;
  }

  .nav-group__pos {
    font-weight: 700;
    color: #101828;
  }

  .nav-group__sep {
    color: #98a2b3;
    font-weight: 400;
  }

  .nav-group__total {
    color: #667085;
    font-weight: 400;
  }

  .nav-group__page-badge {
    display: inline-flex;
    align-items: center;
    padding: 0 14px;
    border-left: 1px solid #d0d5dd;
    border-right: 1px solid #d0d5dd;
    background: #f9fafb;
    font-size: 13px;
    color: #667085;
    white-space: nowrap;
    user-select: none;
  }

  /* Sticky footer */
  .sticky-bottom {
    position: sticky;
    bottom: 0;
    z-index: 1000;
    border-top: 1px solid #e0e0e0;
    background: white;
  }

  /* Overlay on hover */
  .position-relative {
    position: relative;
  }

  .row-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(255, 255, 255, 0.6);
    backdrop-filter: blur(0.5px);
    display: none;
    justify-content: center;
    align-items: center;
    z-index: 5;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .tabel-row:hover .row-overlay {
    display: flex;
  }

  .bg-white {
    background-color: white !important;
  }

  .not_found_product {
    opacity: 0.5;
  }

  .not_found_product:hover {
    opacity: 0.8;
  }

  .in-current-matches-row {
    background-color: #e8f5e9;
  }

  .in-current-matches-row:hover {
    background-color: #c8e6c9 !important;
  }

  .exact-match-row {
    background-color: #ede7f6;
  }

  .exact-match-row:hover {
    background-color: #d1c4e9 !important;
  }

  .swap-product-card {
    flex: 1;
    border-radius: 10px;
    padding: 14px;
    min-width: 0;
  }

  .swap-product-card--old {
    background: #fff3f3;
    border: 1px solid #ffcdd2;
  }

  .swap-product-card--new {
    background: #f1f8f1;
    border: 1px solid #c8e6c9;
  }

  .swap-product-card__label {
    display: flex;
    align-items: center;
  }

  .swap-product-card__img-wrap {
    width: 100%;
    height: 120px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fff;
    border-radius: 6px;
    overflow: hidden;
  }

  .swap-product-card__img {
    max-width: 100%;
    max-height: 120px;
    object-fit: contain;
  }

  .swap-product-card__name {
    color: #1565c0;
    text-decoration: none;
    line-height: 1.3;
  }

  .swap-product-card__name:hover {
    text-decoration: underline;
  }

  .pending-row {
    background-color: #e8f5e9;
    border-left: 3px solid #43a047;
    cursor: pointer;
  }

  .pending-row:hover {
    background-color: #c8e6c9 !important;
  }

  .pending-remove-btn .pending-remove-btn__remove {
    display: none;
  }

  .pending-row:hover .pending-remove-btn .pending-remove-btn__check {
    display: none;
  }

  .pending-row:hover .pending-remove-btn .pending-remove-btn__remove {
    display: inline-flex;
  }

  .section-group-header td {
    cursor: default !important;
    font-size: 12px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08) !important;
  }

  /* Responsive */
  @media (max-width: 960px) {
    .reference-product-row {
      flex-direction: column;
      align-items: flex-start !important;
    }

    .reference-image-wrapper {
      margin-bottom: 12px;
    }
  }
</style>
